"use client";

import { useContext, useEffect, useMemo, useRef, useState } from "react";

import { usePlasmicCanvasContext, usePlasmicQueryData } from "@stadium/services/plasmic";

import HorizontalCarouselWrapper from "@components/swagmagic/dynamic/horizontal-carousel-wrapper";

import { ControlContextSlugs } from "@stadium/shared/src/helpers/plasmic/static-slices";

import LinkWithLang from "@components/swagmagic/dynamic/slices/client-link-with-lang";
import BrandCard from "@components/swagmagic/dynamic/cards/brand";
import DropDown from "@components/swagmagic/dynamic/horizontal-carousel-wrapper/drop-down";

import { CatalogEntriesProps } from "@components/swagmagic/dynamic/slices/catalog-entries";

import { BRAND_LINK_TYPE } from "@stadium/shared/src/constants/stores/routes";

import type { LinkRouteDefaults } from "@stadium/shared/src/components/link-with-lang";

import { queryVariables } from "@helpers/plasmic/products";
import {
  CATEGORY_BRAND_CAROUSEL,
  DEFAULT,
  getHeaderButtonLinkDataAndType,
} from "@helpers/plasmic/slices";
import { getBrandsCarouselData } from "@helpers/plasmic/brand-carousel";
import { getFormattedBrandValues } from "@helpers/products/search";
import { getProductListingData } from "@helpers/listing";

import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";
import { MobileScrollWrapper } from "@components/swagmagic/dynamic/mobile-scroll-wrapper";

import { ALL_PRODUCTS } from "@constants/routes";

import styles from "./main.module.scss";

export type Brand = {
  id: string;
  key: string;
  name: string;
  logo?: string | { url: string };
};

type Category = {
  code: string;
  name?: string;
};

export interface BrandCarouselProps extends CatalogEntriesProps {
  controlContextData: {
    slugs: ControlContextSlugs;
    renderedSlugs: ControlContextSlugs;
    categorySlugs: ControlContextSlugs;
  };
  store: any;
  menu: any;
  menuMapping?: Record<string, any>;
  categories: Category[];
  customBrands?: Brand[];
  hideCategorySelector?: boolean;
}

const renderBrands = (brands: Brand[], linkRouteDefaults?: LinkRouteDefaults) => {
  return brands.map((brand) => {
    return (
      <LinkWithLang
        {...linkRouteDefaults}
        type={BRAND_LINK_TYPE}
        data={{ key: brand.key }}
        target="_self"
        key={brand.key}
      >
        <BrandCard
          src={typeof brand.logo === "string" ? brand.logo : brand.logo?.url ?? ""}
          title={brand.name}
        />
      </LinkWithLang>
    );
  });
};

const BrandCarousel = (props: BrandCarouselProps) => {
  const {
    className,
    viewType,
    controlContextData,
    title,
    description,
    btnText,
    btnLinkType,
    btnLinkOtherUrl,
    btnLinkCategorySlug,
    linkToNewTab,
    categories,
    visibleBrandCount,
    countryCode,
    store,
    menu,
    menuMapping,
    categorySlug,
    hideCategorySelector,
    customBrands,
    setControlContextData,
    linkRouteDefaults,
  } = props;

  const initialCategoryCode = categorySlug || categories[0]?.code;
  const [selectedCategoryCode, setSelectedCategoryCode] = useState(initialCategoryCode);

  const { isDesktopDevice: isDesktop } = useContext(CurrentDeviceContext);

  const isPreviewMode = usePlasmicCanvasContext();

  const onlyHasAllProductsMenu = useMemo(() => {
    const menuList = menu?.children || [];
    return menuList.length === 1 && menuList[0]?.code === ALL_PRODUCTS;
  }, [menu]);

  const country = { iso: countryCode };

  const { data: fetchedBrands = [], isLoading: isBrandsLoading } = usePlasmicQueryData(
    [
      CATEGORY_BRAND_CAROUSEL,
      onlyHasAllProductsMenu,
      store,
      menu,
      countryCode,
      menuMapping,
      selectedCategoryCode,
    ],
    async () => {
      const variables = queryVariables({
        store,
        menu,
        countryIso: isPreviewMode ? "" : countryCode,
        menuMapping,
        categorySlug: selectedCategoryCode,
        productCount: 1,
        identifier: "",
        viewType: DEFAULT,
        skipAggregations: false,
      });

      if (!onlyHasAllProductsMenu && menu && variables.taxonIds?.length) {
        const { data } = await getProductListingData({
          country,
          variables,
          store,
          fetchBrandsOnly: true,
        });
        const brands = data?.baseSearch?.aggregations?.brands?.filter((brand: Brand) => brand.name);
        return getFormattedBrandValues(brands, true) as Brand[];
      }
      return [];
    },
  );

  const { brandsToRender, defaultSlidesPerRow, hasBrands } = useMemo(
    () =>
      getBrandsCarouselData({
        fetchedBrands,
        viewType,
        customBrands,
        visibleBrandCount,
      }),
    [fetchedBrands, viewType, customBrands, visibleBrandCount],
  );

  const brandSlugs = useMemo<ControlContextSlugs>(
    () =>
      fetchedBrands.map((brand) => ({
        value: brand.key,
        label: brand.name,
      })),
    [fetchedBrands],
  );

  // Use ref to track previous brandSlugs and keep latest controlContextData
  const prevBrandSlugsKeyRef = useRef<string>();
  const controlContextDataRef = useRef(controlContextData);

  // Keep ref updated with latest controlContextData
  useEffect(() => {
    controlContextDataRef.current = controlContextData;
  }, [controlContextData]);

  useEffect(() => {
    if (hasBrands && setControlContextData) {
      const brandSlugsKey = JSON.stringify(brandSlugs);

      // Only update if the actual brandSlugs changed (not just reference)
      if (prevBrandSlugsKeyRef.current !== brandSlugsKey) {
        prevBrandSlugsKeyRef.current = brandSlugsKey;
        setControlContextData({
          ...controlContextDataRef.current,
          brandSlugs,
        });
      }
    }
  }, [hasBrands, setControlContextData, brandSlugs]);

  if (!isBrandsLoading && !hasBrands && hideCategorySelector) {
    return <div className="no-brand-content" />;
  }

  const linkDataAndType = getHeaderButtonLinkDataAndType({
    linkType: btnLinkType,
    otherUrl: btnLinkOtherUrl,
    categorySlug: btnLinkCategorySlug || selectedCategoryCode,
  });

  return (
    <HorizontalCarouselWrapper
      className={`${styles.horizontalCarouselWrapper} ${className}`}
      title={title}
      description={description}
      btnText={btnText}
      linkToNewTab={linkToNewTab}
      slidesPerRow={defaultSlidesPerRow}
      linkDataAndType={linkDataAndType}
      linkRouteDefaults={linkRouteDefaults}
      dropDown={
        hideCategorySelector ? null : (
          <DropDown
            dropDownItems={categories}
            fieldValue={selectedCategoryCode}
            setFieldValue={setSelectedCategoryCode}
          />
        )
      }
      showLoader={isBrandsLoading}
      showWithCarousel={hasBrands && isDesktop}
    >
      {isDesktop ? (
        !isBrandsLoading && renderBrands(brandsToRender, linkRouteDefaults)
      ) : (
        <>
          {!isBrandsLoading && (
            <MobileScrollWrapper
              containerClassName={styles.mobileScrollContainer}
              scrollTrackClassName={styles.scrollTrack}
              scrollThumbClassName={styles.scrollThumb}
              isLoading={isBrandsLoading}
            >
              {brandsToRender.map((brand) => {
                return (
                  <div key={brand.key} className={styles.mobileCardWrapper}>
                    <LinkWithLang
                      {...linkRouteDefaults}
                      type={BRAND_LINK_TYPE}
                      data={{ key: brand.key }}
                      target="_self"
                    >
                      <BrandCard
                        src={typeof brand.logo === "string" ? brand.logo : brand.logo?.url ?? ""}
                        title={brand.name}
                      />
                    </LinkWithLang>
                  </div>
                );
              })}
            </MobileScrollWrapper>
          )}
        </>
      )}
    </HorizontalCarouselWrapper>
  );
};

export default BrandCarousel;
